import clsx from 'clsx'
import Image from 'next/image'

import Link from '@/atoms/Link/Link'
import { getFormatRgbaColor, stripHtmlTags } from '@/utils/functions/utils'

/* assets */
import { ReactComponent as ArrowIcon } from 'public/assets/icons/chevron-right.svg'
import { ReactComponent as InstagramIcon } from 'public/assets/influencer/icons/social/Instagram.svg'
import { ReactComponent as TwitterIcon } from 'public/assets/influencer/icons/social/Twitter.svg'
import { ReactComponent as LinkedInIcon } from 'public/assets/influencer/icons/social/Linkedin.svg'

interface Props {
  className?: string
  image?: StrapiImageFile
  name: string
  designation: string
  description?: string
  slug: string
  themeColor: string
  links: SocialLinks
  ctaTitle?: string
  type: 'default' | 'extended' | 'extendedSmall'
}

type SocialLinks = {
  instagram?: string
  twitter?: string
  linkedin?: string
}

function InfluencerCard(props: Props): JSX.Element {
  switch (props.type) {
    case 'default':
      return <DefaultCard {...props} />
    case 'extended':
      return <ExtendedCard {...props} />
    case 'extendedSmall':
      return <ExtendedSmallCard {...props} />
    default:
      return <DefaultCard {...props} />
  }
}

function DefaultCard({
  className,
  name,
  designation,
  description,
  image,
  slug,
  themeColor,
  links,
  ctaTitle = 'Read More'
}: Props) {
  return (
    <div className={clsx('flex flex-col', className)}>
      {image && (
        <Image
          src={image.url}
          alt={image.alternativeText ?? ''}
          width={image.width}
          height={image.height}
          className="rounded-t-lg object-cover | w-full h-72"
        />
      )}
      <div
        className="grow flex flex-col | rounded-b-lg | p-3 pt-1 lg:px-8 lg:py-7"
        style={{ backgroundColor: getFormatRgbaColor(themeColor) }}
      >
        <h3 className="text-lg lg:text-h3-mobile font-medium text-blue-web-90 | mb-1 lg:mb-3">
          {name}
        </h3>
        <p className="text-sm font-medium text-gray-80 line-clamp-2 | mb-3 lg:mb-5">
          {designation}
        </p>
        {description && (
          <div
            className="text-xs text-gray-80 line-clamp-4 | mb-10"
            dangerouslySetInnerHTML={{ __html: stripHtmlTags(description) }}
          />
        )}
        <div className="flex items-center justify-between | mt-auto">
          <Link
            href={slug}
            className="text-xs text-blue-50 font-medium | flex items-center gap-1"
          >
            <span>{ctaTitle}</span>
            <ArrowIcon />
          </Link>
          <SocialLinks links={links} />
        </div>
      </div>
    </div>
  )
}

function ExtendedCard({
  className,
  name,
  designation,
  image,
  slug,
  themeColor,
  links,
  ctaTitle = 'Read More'
}: Props) {
  return (
    <div className={className}>
      {image && (
        <Image
          src={image.url}
          alt={image.alternativeText ?? ''}
          width={image.width}
          height={image.height}
          className="rounded-t-lg | w-full"
        />
      )}
      <div
        className="rounded-b-lg | px-8 py-7"
        style={{ backgroundColor: getFormatRgbaColor(themeColor) }}
      >
        <h3 className="h2-mobile font-medium text-gray-80 | mb-3">{name}</h3>
        <p className="text-lg text-gray-80 | mb-10 | line-clamp-2">
          {designation}
        </p>
        <div className="flex items-center justify-between">
          <Link
            href={slug}
            className="text-small text-blue-50 font-medium | flex items-center gap-1"
          >
            <span>{ctaTitle}</span>
            <ArrowIcon />
          </Link>
          <SocialLinks links={links} />
        </div>
      </div>
    </div>
  )
}

export function ExtendedSmallCard({
  className,
  name,
  designation,
  image,
  slug,
  themeColor,
  links,
  ctaTitle = 'Read More'
}: Props) {
  return (
    <div className={clsx(className, 'flex flex-col | h-full')}>
      {image && (
        <Image
          src={image.url}
          alt={image.alternativeText ?? ''}
          width={image.width}
          height={image.height}
          className="rounded-t | w-full | aspect-[3/2.8] object-cover"
        />
      )}
      <div
        className="rounded-b | flex flex-col justify-between | p-3 lg:px-8 lg:py-7 | h-full"
        style={{ backgroundColor: getFormatRgbaColor(themeColor) }}
      >
        <h3 className="text-sm md:text-3xl font-medium text-blue-web-90 | mb-2 md:mb-3">
          {name}
        </h3>
        <p className="text-caption md:text-lg text-gray-80 line-clamp-2 | mb-5 md:mb-10">
          {designation}
        </p>
        <div className="flex items-center justify-between">
          <Link
            href={slug}
            className="text-caption text-blue-50 font-medium | flex items-center gap-1"
          >
            <span>{ctaTitle}</span>
            <ArrowIcon />
          </Link>
          <SocialLinks links={links} />
        </div>
      </div>
    </div>
  )
}

function SocialLinks(props: { links: SocialLinks; className?: string }) {
  return (
    <div className={props.className}>
      <div className="flex justify-center | gap-2 lg:gap-4">
        {props.links.instagram && (
          <Link
            href={props.links.instagram}
            target="_blank"
            rel="noopener noreferrer"
          >
            <InstagramIcon color="#313131" className="h-4 w-4" />
          </Link>
        )}
        {props.links.twitter && (
          <Link
            href={props.links.twitter}
            target="_blank"
            rel="noopener noreferrer"
          >
            <TwitterIcon color="#313131" className="h-4 w-4" />
          </Link>
        )}
        {props.links.linkedin && (
          <Link
            href={props.links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon color="#313131" className="h-4 w-4" />
          </Link>
        )}
      </div>
    </div>
  )
}

export default InfluencerCard
